<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { LocationQuery, useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { DgrCheckbox, DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import PaginationNavigator from '@/components/common/molecules/pagination-navigator.vue';
import {
  SnackbarParams,
  useSnackbar,
} from '@/components/common/snackbar/use-snackbar';
import {
  AnewsStorageSearchOrder,
  AnewsStorageSearchParams,
  AnewsStorageSearchSort,
  AnewStorageFile,
  DeleteAnewsStorageFilesParams,
  DeleteAnewStorageFilesResult,
  Pagination,
} from '@/types';
import { fileIconName, formatUsageFileSize } from '@/utils/user-document/file';

const ROUTE_NAME = 'anewsStorageIndex';

const props = defineProps<{ processing: boolean }>();
const emit = defineEmits<{ deleteCallback: [] }>();

const route = useRoute();
const router = useRouter();
const { createSnackbar } = useSnackbar();
const fileName = ref<string>((route.query.file_name as string) || ''); // 検索条件
const checkSelectAll = ref(false); // すべて選択をチェックを判定
// 一度すべて選択をして、1つのファイルのチェックを外した時には
// すべて選択のチェックは外れるが,checkDeleteFilesのフラグは変わらない
// これにより他のファイルのチェック状態を維持したままにする
const checkDeleteFiles = ref(false);
const excludeDeleteFiles = ref<Set<number>>(new Set<number>());
const deleteFiles = ref<Set<number>>(new Set<number>());
const deleteFileAmount = ref<number>(0);
const isDeleteModalOpen = ref<boolean>(false);
const files = ref<AnewStorageFile[]>([]);
const condition = ref<AnewsStorageSearchParams>({});
const pagination = ref<Pagination>({
  total_pages: 0,
  limit_value: 20,
  current_page: 1,
  total_count: 0,
  first_page: true,
  last_page: true,
});

// 現在同期中の場合は削除ボタンは押せない
const isDisabledDeleteButton = computed(
  () => props.processing || deleteFileAmount.value === 0,
);
const deleteButtonToolTipMessage = computed(() => {
  return props.processing && isDisabledDeleteButton.value
    ? '同期処理中のため現在削除ができません'
    : '';
});

// すべて削除
const toggleDeleteAllFiles = (checked: boolean) => {
  checkSelectAll.value = checked;
  checkDeleteFiles.value = checked;

  // 混乱を招かないように選択した削除ファイル/除外ファイルの情報はクリアする
  deleteFiles.value.clear();
  excludeDeleteFiles.value.clear();
  if (checked) {
    deleteFileAmount.value = pagination.value.total_count;
  } else {
    deleteFileAmount.value = 0;
  }
};

// 削除のチェックを入れたとき
const toggleFileDeleteEvent = (fileId: number, checked: boolean) => {
  if (checked) {
    deleteFiles.value.add(fileId);
    deleteFileAmount.value += 1;
    excludeDeleteFiles.value.delete(fileId);

    // 削除するファイル件数とAPIから取得した件数が一致する場合はすべて削除と同じ扱いにする
    if (deleteFileAmount.value === pagination.value.total_count) {
      toggleDeleteAllFiles(true);
    }
  } else {
    deleteFiles.value.delete(fileId);
    deleteFileAmount.value -= 1;

    // もしチェックを外して、すべて選択にチェックが入れていた場合は、すべて選択のチェックを外す
    if (checkDeleteFiles.value) {
      checkSelectAll.value = false;
      excludeDeleteFiles.value.add(fileId); // 全削除から除外するファイル
    }
  }
};

// 削除ボタンを押したときに確認ダイアログを出す
const confirmDeleteFilesEvent = () => {
  isDeleteModalOpen.value = true;
};

// 実際にファイルを削除するイベント
const deleteFilesEvent = async () => {
  // ファイル削除のAPIを実行する
  // 409が返ってきたら、同期中なので
  const params: DeleteAnewsStorageFilesParams = {};

  // ファイルが指定されていればそれだけを使う
  if (deleteFiles.value.size !== 0) {
    params.ids = Array.from(deleteFiles.value);

    // すべて選択に1回チェックを入れたとき
  } else if (checkDeleteFiles.value) {
    params.is_delete_all = true;
    params.delete_file_name = condition.value.file_name;
    params.exclude_delete_file_ids = Array.from(excludeDeleteFiles.value);
  }

  try {
    const response = await api.deleteAnewsStorageFile(params);
    createSnackbar(makeSnackbarParams(response));

    // 表示されているページのデータが全部消えている可能性があるので、1ページ目に戻って検索を再度行う
    await router.replace({
      name: ROUTE_NAME,
      query: { ...condition.value, page: undefined },
    });

    emit('deleteCallback');
  } catch (e) {
    createSnackbar({ type: 'error', message: 'エラーが発生しました' });
    throw e; // Sentryに通知するためエラーを大域例外に投げる
  } finally {
    isDeleteModalOpen.value = false;
  }
};

const makeSnackbarParams = (
  params: DeleteAnewStorageFilesResult,
): SnackbarParams => {
  switch (params) {
    case 'no_content':
      return { type: 'success', message: '削除しました' };
    case 'syncing':
      return { type: 'error', message: '同期処理中のため現在削除ができません' };
    default:
      return { type: 'error', message: 'エラーが発生しました' };
  }
};

const isCheckDeleteFile = (fileId: number) => {
  if (checkSelectAll.value || deleteFiles.value.has(fileId)) return true;

  // すべて選択を一度選んで、その後にそのファイルだけチェックを外した場合はチェックされない
  // その他のファイルはチェックされたままとなる
  if (excludeDeleteFiles.value.has(fileId)) return false;

  // 一度すべて選択をして、他のファイルのチェックを外した時には
  // すべて選択のチェックは外れるが,checkDeleteFilesのフラグは変わらない
  return checkDeleteFiles.value;
};

// ページ番号の計算
const beginCount = computed(() => {
  const value = pagination.value;
  if (value.first_page) return 1;

  return (value.current_page - 1) * value.limit_value + 1;
});

const endCount = computed(() => {
  const value = pagination.value;
  if (value?.last_page) return value.total_count;

  return value.current_page * value.limit_value;
});

// 検索
const searchButtonEventHandler = async () => {
  toggleDeleteAllFiles(false);
  await router.push({
    name: ROUTE_NAME,
    query: { file_name: fileName.value },
  });
};

const search = async (params: AnewsStorageSearchParams) => {
  const result = await api.getAnewsStorageFiles(params);
  files.value = result.data;
  condition.value = result.condition;
  pagination.value = result.pagination;
};

const makeSearchParamsFromQuery = (): AnewsStorageSearchParams => {
  const { file_name, sort, order, page } = route.query;
  return {
    file_name: file_name?.toString(),
    sort: isValidSort(sort) ? sort : undefined,
    order: isValidOrder(order) ? order : undefined,
    page: isNaN(Number(page)) ? undefined : Number(page),
  };
};

const isValidSort = (
  value: LocationQuery[keyof LocationQuery],
): value is AnewsStorageSearchSort => {
  value = Array.isArray(value) ? value[0] : value;
  return (
    value === 'last_modified_at' || value === 'status' || value === 'file_size'
  );
};

const isValidOrder = (
  value: LocationQuery[keyof LocationQuery],
): value is AnewsStorageSearchOrder => {
  value = Array.isArray(value) ? value[0] : value;
  return value === 'asc' || value === 'desc';
};

const toggleSortFileSize = (): Pick<
  AnewsStorageSearchParams,
  'sort' | 'order' | 'page'
> => {
  const order = toggleOrder('file_size');
  return {
    sort: 'file_size',
    order,
    page: 1,
  };
};

const toggleSortStatus = (): Pick<
  AnewsStorageSearchParams,
  'sort' | 'order' | 'page'
> => {
  const order = toggleOrder('status');
  return {
    sort: 'status',
    order,
    page: 1,
  };
};

const toggleOrder = (sort: AnewsStorageSearchSort) => {
  // 現在のソート内容と同じソート内容を指定した時はasc,descを切り替え
  if (condition.value.sort === sort) {
    return condition.value.order === 'asc' ? 'desc' : 'asc';
  }
  // 画面上のその他の項目の初期表示は昇順表示になっているので
  // クリックしたら降順にソートする
  return 'desc';
};

onMounted(async () => await search(makeSearchParamsFromQuery()));

watch(
  () => route.query,
  async () => await search(makeSearchParamsFromQuery()),
);
</script>

<template>
  <div class="file-list-container">
    <div class="search-container">
      <DgrIcon name="search" class="search-icon" size="small" />
      <input
        name="file_name"
        type="search"
        class="c-text-regular--m search-input"
        placeholder="ファイルを検索"
        v-model="fileName"
        @change="searchButtonEventHandler"
      />
    </div>
    <div class="c-text-regular--m file-count">
      ファイル {{ pagination.total_count }}件
    </div>
    <template v-if="files.length > 0">
      <div class="delete-area">
        <DgrCheckbox
          name="all-delete"
          value="0"
          :model-value="checkSelectAll"
          @update:model-value="
            (checked: boolean) => toggleDeleteAllFiles(checked)
          "
        >
          <span class="c-text-regular--m">すべて選択</span>
        </DgrCheckbox>
        <div
          class="c-text-regular--m delete-file-count"
          v-if="deleteFileAmount > 0"
        >
          {{ deleteFileAmount }}件のファイルが選択されています
        </div>
        <button
          class="delete-button c-btn c-outlineBtn--auto c-outlineBtn--secondary"
          :disabled="isDisabledDeleteButton"
          v-tooltip.top="deleteButtonToolTipMessage"
          @click="confirmDeleteFilesEvent"
        >
          <DgrIcon
            :keep-fill="false"
            name="trash"
            class="trash-icon"
            size="small"
          />
          削除
        </button>
      </div>
      <table class="file-list-table">
        <thead>
          <tr class="header">
            <th class="file-check-delete-header"></th>
            <th class="file-name-header c-text-regular--s">名前</th>
            <th class="file-size-header c-text-regular--s">
              <router-link
                :to="{
                  name: ROUTE_NAME,
                  query: { ...condition, ...toggleSortFileSize() },
                }"
              >
                <div class="header-container">
                  <div>ファイルサイズ</div>
                  <DgrIcon
                    name="long-arrow-down"
                    size="small"
                    v-if="
                      condition.sort === 'file_size' &&
                      condition.order === 'desc'
                    "
                  />
                  <DgrIcon name="long-arrow-up" size="small" v-else />
                </div>
              </router-link>
            </th>
            <th class="file-status-header c-text-regular--s">
              <router-link
                :to="{
                  name: ROUTE_NAME,
                  query: { ...condition, ...toggleSortStatus() },
                }"
              >
                <div class="header-container">
                  <div>ステータス</div>
                  <DgrIcon
                    name="long-arrow-down"
                    size="small"
                    v-if="
                      condition.sort === 'status' && condition.order === 'desc'
                    "
                  />
                  <DgrIcon name="long-arrow-up" size="small" v-else />
                </div>
              </router-link>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="file in files" :key="file.id">
            <td>
              <DgrCheckbox
                name="delete-file"
                value="0"
                :model-value="isCheckDeleteFile(file.id)"
                @update:model-value="
                  (checked: boolean) => toggleFileDeleteEvent(file.id, checked)
                "
              />
            </td>
            <td class="file-name-row">
              <div class="file-name-container">
                <div>
                  <DgrIcon
                    class="check-icon"
                    :name="fileIconName(file.file_type)"
                  />
                </div>
                <div class="file-name">{{ file.file_name }}</div>
              </div>
            </td>
            <td>
              {{ formatUsageFileSize(file.file_size_bytes, -1) }}
            </td>
            <td v-if="file.file_status === 'processing'">
              <DgrIcon
                :keep-fill="false"
                name="chart-round-bubble"
                class="name-icon synced"
              />
              同期処理中
            </td>
            <td v-else-if="file.file_status === 'ready'">
              <DgrIcon
                :keep-fill="false"
                name="check"
                class="name-icon synced"
              />
              同期処理完了
            </td>
            <td v-else></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="footer">
              <div class="pagination">
                <div class="c-text-regular--m count">
                  {{ beginCount }}〜{{ endCount }}件目 /
                  {{ pagination.total_count }}件
                </div>
                <PaginationNavigator
                  :current-page="pagination.current_page"
                  :total-pages="pagination.total_pages"
                />
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </template>
  </div>
  <DgrDialog
    v-model:open="isDeleteModalOpen"
    :close-on-escape="false"
    :close-on-interact-outside="false"
    :modal="false"
    title="ファイルを削除"
    submit-label="削除"
    submit-style="danger"
    @submit="deleteFilesEvent"
  >
    <template #description>
      <div class="c-text-regular--m dialog-message">
        選択した{{ deleteFileAmount }}件のファイルを削除します。<br />
        削除すると元に戻せません。
      </div>
    </template>
  </DgrDialog>
</template>

<style scoped lang="scss">
.file-list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .search-container {
    background-color: $color-white;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    padding: 0 12px;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  .search-input {
    flex: 1 0 auto;
    border: none;

    &:focus {
      outline: none;
    }
  }

  .file-count {
    display: flex;
    flex-direction: row;
    gap: 4px;
  }

  .delete-area {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .delete-all-label {
      color: $color-gray1000;
    }

    .delete-file-count {
      flex: 1 0 auto;
      color: $color-green600;
    }

    .trash-icon {
      margin-right: 4px;
    }

    .delete-button {
      background-color: $color-white;
    }
  }

  .file-list-table {
    background-color: $color-white;
    border: 1px solid $color-gray400;
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 4px;

    th {
      padding: 16px;
      text-align: left;
      text-wrap: nowrap;
    }

    td {
      border-top: 1px solid $color-gray400;
      padding: 16px;
      text-align: left;
      text-wrap: nowrap;
    }

    td:first-of-type {
      text-align: center;
    }

    .file-check-delete-header {
      width: 5%;
    }

    .file-name-header {
      width: 55%;
    }

    .file-size-header {
      width: 20%;
    }

    .header-container {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    .file-status-header {
      width: 20%;
    }

    .file-name-row {
      max-width: 500px;
    }

    .file-name-container {
      display: flex;
      flex-direction: row;
      gap: 4px;
    }

    .file-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .footer {
      padding-top: 24px;
    }
  }

  .name-icon {
    display: inline-block;
    margin-right: 10px;
    vertical-align: middle;

    &.syncing {
      color: $color-gray800;
    }
    &.synced {
      color: $color-green600;
    }
  }

  .pagination {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
  }
}

.dialog-message {
  width: 480px;
}
</style>
